import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { closingDocumentActions } from '../actions/closing-document.actions';
import { positionLabelKey } from '../constants/account-positions.constant';
import type { AccountOption } from '../types/closing-documents.types';
import { DocumentDetail } from '../components/document-detail/document-detail.types';
import { ClosingDocumentService } from '../services/closing-document.service';
import {
    DocumentFilterCounts,
    DocumentFilterKey,
    DocumentRow,
    DocumentSort,
    SyncReportView,
} from '../components/documents-hub/documents-hub.types';
import {
    IClosingAccountOption,
    ISetAssignmentRequest,
    ISharepointFetchResponse,
    IUploadFailure,
    IUploadState,
    SyncStartRefusal,
} from '../models/closing-document.model';
import {
    selectAccountDocumentRows,
    selectAccountDocumentsError,
    selectAccountDocumentsLoading,
    selectAccounts,
    selectActiveFilter,
    selectAgentOnly,
    selectDocumentCounts,
    selectDocumentDetail,
    selectDocumentDetailError,
    selectDocumentDetailLoading,
    selectDocumentProposalFailed,
    selectDocumentProposalPending,
    selectDocumentWriteFailed,
    selectDocumentWriting,
    selectDocumentRows,
    selectDocumentSort,
    selectDocumentsError,
    selectDocumentsLoading,
    selectPendingRowIds,
    selectUploadingFilenames,
    selectProposalFailedIds,
    selectProposalPendingIds,
    selectSearchTerm,
    selectUploadFailures,
    selectUploadPending,
    selectUploadState,
    selectSyncPending,
    selectLastSync,
    selectSyncReport,
    selectSyncRefusal,
} from '../selectors/closing-document.selectors';

/**
 * Facade for the ABCP closing-documents slice (REV-21376).
 *
 * Pages consume THIS and never reach into the Store directly — same rule as `IntegrationProvider`.
 * Every getter returns exactly one `@Input()` of a pure view, so a host template can bind through the
 * async pipe without mapping anything.
 *
 * ⚠️ THE ASSIGNMENT WRITES MOVED IN (2026-08-20, TL decision). This facade deliberately offered no
 * way to write one, which is what kept the boundary intact while it stood.
 *
 * #84 — `proposalAccept` / `proposalReject` land now: #57's auto-assignment (confidence ≥ 90) makes
 * the machine proposal a real, reachable state, and these two verbs are how a human overrides it.
 */
@Injectable({ providedIn: 'root' })
export class ClosingDocumentProvider {
    public constructor(
        private readonly store: Store,
        // The position catalogue is text, and text is translated. Injected rather than passed in by
        // every host: the alternative is the same map written twice, in two orchestrators.
        private readonly i18next: I18NextPipe,
        // REV-21369 — the byte door. NOT through the store: an ArrayBuffer is not serialisable state,
        // and nothing but the viewer of THIS page ever reads it.
        private readonly documents: ClosingDocumentService,
    ) {}

    // ─── REV-21369 — the document's original bytes (ADR-0021 E3) ─────────────────────────────────
    /** The decrypted original for the PDF viewer. 404 unless the scan is CLEAN. */
    public getContent$(projectId: string, documentId: string): Observable<ArrayBuffer> {
        return this.documents.getContent(projectId, documentId);
    }
    /** The same door as a download — the original, without highlights (Freigabe Q3). */
    public downloadContent$(projectId: string, documentId: string): Observable<Blob> {
        return this.documents.downloadContent(projectId, documentId);
    }

    // ─── Reads ───────────────────────────────────────────────────────────────────────────────────
    public getDocuments$(): Observable<DocumentRow[]> {
        return this.store.select(selectDocumentRows);
    }
    public getCounts$(): Observable<DocumentFilterCounts> {
        return this.store.select(selectDocumentCounts);
    }
    public getActiveFilter$(): Observable<DocumentFilterKey> {
        return this.store.select(selectActiveFilter);
    }
    public getSort$(): Observable<DocumentSort | null> {
        return this.store.select(selectDocumentSort);
    }
    public getSearchTerm$(): Observable<string> {
        return this.store.select(selectSearchTerm);
    }
    /** REV-21901 N3 (W2) — the "automatisch zugewiesen" facet, CONTROLLED from the slice like the pill. */
    public getAgentOnly$(): Observable<boolean> {
        return this.store.select(selectAgentOnly);
    }
    public getLoading$(): Observable<boolean> {
        return this.store.select(selectDocumentsLoading);
    }
    public getError$(): Observable<boolean> {
        return this.store.select(selectDocumentsError);
    }

    public getDocument$(): Observable<DocumentDetail | null> {
        return this.store.select(selectDocumentDetail);
    }
    public getDocumentLoading$(): Observable<boolean> {
        return this.store.select(selectDocumentDetailLoading);
    }
    public getDocumentError$(): Observable<boolean> {
        return this.store.select(selectDocumentDetailError);
    }

    // ─── #84 — the write state of accept/reject/un-file (F5/F9/F10) ────────────────────────────────
    /** The HUB's shape: the SET of row ids writing right now — more than one row can be, at once. */
    public getProposalPendingIds$(): Observable<string[]> {
        return this.store.select(selectProposalPendingIds);
    }
    /** REV-22366 — the files of a hub upload that the list does not hold yet. */
    public getUploadingFilenames$(): Observable<string[]> {
        return this.store.select(selectUploadingFilenames);
    }
    /** REV-22366 — the rows with a write running (Nicht relevant, choosing an account). */
    public getPendingRowIds$(): Observable<string[]> {
        return this.store.select(selectPendingRowIds);
    }
    /** The HUB's shape: the SET of row ids whose last write failed. */
    public getProposalFailedIds$(): Observable<string[]> {
        return this.store.select(selectProposalFailedIds);
    }
    /** The SINGLE-DOCUMENT page's shape: `true` only while it is THIS document's write in flight. */
    public getDocumentProposalPending$(): Observable<boolean> {
        return this.store.select(selectDocumentProposalPending);
    }
    public getDocumentProposalFailed$(): Observable<boolean> {
        return this.store.select(selectDocumentProposalFailed);
    }
    /** The document page: a write for the open document is running. */
    public getDocumentWriting$(): Observable<boolean> {
        return this.store.select(selectDocumentWriting);
    }
    /** The document page: the last write for the open document failed. */
    public getDocumentWriteFailed$(): Observable<boolean> {
        return this.store.select(selectDocumentWriteFailed);
    }

    /**
     * The account plan both pickers list, as the pure views want it. Empty until `loadAccounts`
     * lands, which is a valid state.
     *
     * THE TRANSLATION HAPPENS HERE, and this is the right place for it: every other getter on this
     * facade returns exactly one `@Input()` of a pure view, and `AccountOption` is that input. The
     * store holds the position as the backend's enum KEY, because a store full of German text cannot
     * survive a language switch; the pickers render text, because a dumb view does not know a
     * position catalogue. One map, in the one class that sits between them.
     *
     * `klasse` and `group` carry the SAME fact, and deliberately: `klasse` is the muted text at the
     * right edge of a picker row, `group` is what both pickers MATCH on, and with a real chart the
     * one thing we know about an account beyond number and name is the position it is bound to. The
     * fixture that used to feed this had a separate account class ("Aktiven"); nothing in the real
     * data supplies that, and deriving it from the leading digit would assume every client uses the
     * Swiss KMU chart.
     */
    public getAccounts$(): Observable<AccountOption[]> {
        return this.store
            .select(selectAccounts)
            .pipe(map((accounts) => accounts.map((account) => this.toAccountOption(account))));
    }

    /** One plan entry as a picker row. An unbound account gets the catalogue's own "no position" text. */
    private toAccountOption(account: IClosingAccountOption): AccountOption {
        const position = this.i18next.transform(positionLabelKey(account.accountGroup));

        return {
            id: account.id,
            number: account.number,
            name: account.name,
            klasse: position,
            group: position,
        };
    }

    /** The open account's documents — only what is filed to it, answered by the server. */
    public getAccountDocuments$(): Observable<DocumentRow[]> {
        return this.store.select(selectAccountDocumentRows);
    }
    public getAccountDocumentsLoading$(): Observable<boolean> {
        return this.store.select(selectAccountDocumentsLoading);
    }
    public getAccountDocumentsError$(): Observable<boolean> {
        return this.store.select(selectAccountDocumentsError);
    }

    public getUploadPending$(): Observable<boolean> {
        return this.store.select(selectUploadPending);
    }
    public getUploadFailures$(): Observable<IUploadFailure[]> {
        return this.store.select(selectUploadFailures);
    }
    /** Both in one emission, for a caller that decides on the pair when an upload settles. */
    public getUploadState$(): Observable<IUploadState> {
        return this.store.select(selectUploadState);
    }

    public getSyncPending$(): Observable<boolean> {
        return this.store.select(selectSyncPending);
    }
    /** The last pull's outcome — `created: 0` plus skips is the normal "nothing new" case. */
    public getLastSync$(): Observable<ISharepointFetchResponse | null> {
        return this.store.select(selectLastSync);
    }

    /** REV-21964 · U1 — the last RUN's report, already in the hub's shape. */
    public getSyncReport$(): Observable<SyncReportView | null> {
        return this.store.select(selectSyncReport);
    }
    /** R3/R6 — why the last start was refused. An answer, never an error. */
    public getSyncRefusal$(): Observable<SyncStartRefusal | null> {
        return this.store.select(selectSyncRefusal);
    }

    // ─── Commands ────────────────────────────────────────────────────────────────────────────────
    /**
     * Load this closing's documents with the CURRENT pill and search term.
     *
     * Neither is a parameter, on purpose. The tab page remounts on every return from the
     * single-document route, and a hardcoded `'inbox'` there would silently reset the pill — while a
     * hardcoded empty search would silently UN-NARROW the three counts under a search box that still
     * reads "Bilanz". The slice holds both; the effect reads them.
     */
    public loadDocuments(projectId: string): void {
        this.store.dispatch(closingDocumentActions.reloadDocuments({ projectId }));
    }
    /**
     * Pull the bound SharePoint folders in. Triggered by the hub's "Jetzt synchronisieren" and once
     * automatically when the settings modal closes — never on hub load (product decision 2026-08-19).
     */
    public syncSharepoint(projectId: string): void {
        this.store.dispatch(closingDocumentActions.syncSharepoint({ projectId }));
    }
    /**
     * REV-21964 · R3 — "Jetzt synchronisieren": start a run and come back at once.
     *
     * What the BUTTON calls now. The synchronous pull above stays for the folder-binding trigger,
     * where the set of files is whatever the user just picked and the answer is wanted immediately;
     * the button has to survive a five-hundred-file folder, which does not fit in one request.
     */
    public startSharepointSync(projectId: string): void {
        this.store.dispatch(closingDocumentActions.startSharepointSync({ projectId }));
    }
    /**
     * Read the newest run's report — what the hub asks for on open so the last result is there
     * without the user pressing anything.
     *
     * ⚠️ NOT a pull. This reads our own row; the tab still never calls Microsoft on load.
     */
    public loadLatestSyncRun(projectId: string): void {
        this.store.dispatch(closingDocumentActions.loadSyncRun({ projectId, runId: null }));
    }
    public loadDocument(projectId: string, documentId: string): void {
        this.store.dispatch(closingDocumentActions.loadDocument({ projectId, documentId }));
    }
    public clearDocument(): void {
        this.store.dispatch(closingDocumentActions.clearDocument());
    }
    /** The files the modal already accepted locally — never the raw pick. */
    public uploadDocuments(projectId: string, files: File[], accountId?: string): void {
        this.store.dispatch(
            closingDocumentActions.uploadDocuments({ projectId, files, ...(accountId != null ? { accountId } : {}) }),
        );
    }
    public setNotRelevant(projectId: string, documentId: string, notRelevant: boolean): void {
        this.store.dispatch(closingDocumentActions.setNotRelevant({ projectId, documentId, notRelevant }));
    }

    /** The documents filed to ONE account. Does not touch the hub's list. */
    public loadAccountDocuments(projectId: string, accountId: string): void {
        this.store.dispatch(closingDocumentActions.loadAccountDocuments({ projectId, accountId }));
    }

    /** Load the account plan once for this closing — both pickers and both surfaces share it. */
    /** Load the plan unconditionally. Screens opening a picker want `loadAccountsIfNeeded()`. */
    public loadAccounts(projectId: string): void {
        this.store.dispatch(closingDocumentActions.loadAccounts({ projectId }));
    }

    /**
     * What a screen calls when it opens: "the picker will need this closing's plan."
     *
     * Requests only when the store cannot answer. The plan changes only through an ERP sync, which
     * marks it stale — so a pass through hub → document → hub now costs one request instead of three.
     */
    public loadAccountsIfNeeded(projectId: string): void {
        this.store.dispatch(closingDocumentActions.loadAccountsIfNeeded({ projectId }));
    }

    /** File a document to an account or to the Projekt-Akte. */
    public assignDocument(projectId: string, documentId: string, request: ISetAssignmentRequest): void {
        this.store.dispatch(closingDocumentActions.assignDocument({ projectId, documentId, request }));
    }

    /** Un-file — back to Eingang. */
    public unassignDocument(projectId: string, documentId: string): void {
        this.store.dispatch(closingDocumentActions.unassignDocument({ projectId, documentId }));
    }

    /**
     * F1 (#84) — exactly the names the two `@Output()`s carry and that the three class comments this
     * ticket corrects called "still absent": accept or reject the machine proposal #57 produced.
     */
    public proposalAccept(projectId: string, documentId: string, accountId?: string): void {
        this.store.dispatch(closingDocumentActions.acceptProposal({ projectId, documentId, accountId }));
    }
    public proposalReject(projectId: string, documentId: string): void {
        this.store.dispatch(closingDocumentActions.rejectProposal({ projectId, documentId }));
    }

    /** Dauerakte on its own, without touching the filing (ADR-0009 §5). */
    public setDocumentValidity(projectId: string, documentId: string, validity: 'closing' | 'longTerm'): void {
        this.store.dispatch(closingDocumentActions.setDocumentValidity({ projectId, documentId, validity }));
    }
    /**
     * Answering these is what makes the hub's controlled pill/sort/search actually move — the view
     * renders all three from its `@Input()`s and never mutates them, so an unanswered click sticks.
     */
    public setFilter(filter: DocumentFilterKey): void {
        this.store.dispatch(closingDocumentActions.setFilter({ filter }));
    }
    public setSort(sort: DocumentSort | null): void {
        this.store.dispatch(closingDocumentActions.setSort({ sort }));
    }
    public setSearchTerm(searchTerm: string): void {
        this.store.dispatch(closingDocumentActions.setSearchTerm({ searchTerm }));
    }
    /**
     * REV-21901 N3 (W2) — the facet toggle. Like `setFilter`, its effect reloads from the SERVER
     * (`assignedBy=agent`): which rows are machine-assigned is the server's answer, not a local
     * predicate over the loaded page.
     */
    public setAgentOnly(agentOnly: boolean): void {
        this.store.dispatch(closingDocumentActions.setAgentOnly({ agentOnly }));
    }
}
